import React, { useState, useRef, memo } from "react";
import {
  Box,
  Button,
  Popover,
  TextField,
  InputAdornment,
  IconButton,
  ListItemButton,
  Checkbox,
  ListItemText,
  Typography,
  Grow,
  useTheme,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { FixedSizeList, ListChildComponentProps } from "react-window";

export interface MultiSelectDropdownProps<T> {
  label: string;
  items: Array<T & { isChecked: boolean }>;
  keySelector: (item: T) => string | number;
  labelSelector: (item: T) => string;
  onChange: (updated: Array<T & { isChecked: boolean }>) => void;
  error?: boolean;
  helperText?: string;
}

export function MultiSelectDropdown<T>({
  label,
  items,
  keySelector,
  labelSelector,
  onChange,
  error,
  helperText,
}: MultiSelectDropdownProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchText, setSearchText] = useState("");
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const theme = useTheme();

  const filteredItems = items.filter((item) =>
    labelSelector(item)?.toLowerCase().includes(searchText?.toLowerCase()),
  );

  const selectedItems = items.filter((item) => item.isChecked);
  const isAllSelected = filteredItems.length > 0 && filteredItems.every((item) => item.isChecked);

  const displayText = selectedItems.map(labelSelector).join(", ") || `Select ${label}`;

  const handleToggle = (key: string | number) => {
    const updated = items.map((item) =>
      keySelector(item) === key ? { ...item, isChecked: !item.isChecked } : item,
    );
    onChange(updated);
  };

  const handleSelectAll = (checked: boolean) => {
    const updated = items.map((item) =>
      filteredItems.includes(item) ? { ...item, isChecked: checked } : item,
    );
    onChange(updated);
  };

  const open = Boolean(anchorEl);
  const focus = open;

  // Virtualized Row
  const Row = memo(({ index, style }: ListChildComponentProps) => {
    if (index === 0) {
      // Select All row
      return (
        <div style={style}>
          <ListItemButton sx={{ py: 0, px: 1 }} onClick={() => handleSelectAll(!isAllSelected)}>
            <Checkbox
              size="small"
              checked={isAllSelected}
              indeterminate={!isAllSelected && filteredItems.some((item) => item.isChecked)}
            />
            <ListItemText
              primary={
                <Typography fontWeight="bold" fontSize="0.875rem">
                  Select All
                </Typography>
              }
            />
          </ListItemButton>
        </div>
      );
    }

    const item = filteredItems[index - 1]; // -1 for Select All
    return (
      <div style={style}>
        <ListItemButton sx={{ py: 0, px: 1 }} onClick={() => handleToggle(keySelector(item))}>
          <Checkbox size="small" checked={item.isChecked} />

          <ListItemText
            primary={
              <Tooltip title={labelSelector(item)} arrow>
                <Typography noWrap>{labelSelector(item)}</Typography>
              </Tooltip>
            }
          />
        </ListItemButton>
      </div>
    );
  });

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <label
        htmlFor="btnMultiSelect"
        className="floating-label"
        style={
          focus
            ? { color: theme.palette.warning.main }
            : error
              ? { color: theme.palette.error.main }
              : undefined
        }
      >
        {label}
        {error && " *"}
      </label>

      <Button
        id="btnMultiSelect"
        ref={buttonRef}
        onClick={() => setAnchorEl(buttonRef.current)}
        endIcon={<ArrowDropDownIcon />}
        variant="outlined"
        size="small"
        fullWidth
        sx={{
          height: "40px",
          justifyContent: "space-between",
          textTransform: "none",
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          border: `1px solid ${error ? theme.palette.error.main : focus ? theme.palette.warning.main : "rgba(161, 159, 159, 0.6)"}`,
          borderRadius: 2,
          boxShadow: error
            ? `0 0 0 1px ${theme.palette.error.main}`
            : focus
              ? `0 0 0 1px ${theme.palette.warning.light}`
              : "none",
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexGrow: 1,
            textAlign: "left",
            color: "#666",
          }}
          title={displayText}
        >
          {displayText}
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={Grow}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box px={1} pb={0.5}>
          <TextField
            placeholder={`Search ${label?.toLowerCase()}...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ width: 300, height: 300 }}>
          <FixedSizeList
            height={300}
            width="100%"
            itemSize={36}
            itemCount={filteredItems.length + 1} // +1 for Select All
            overscanCount={5}
          >
            {Row}
          </FixedSizeList>
        </Box>
      </Popover>
      {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
    </Box>
  );
}
